import { useEffect, useRef } from "react";

const BASE = process.env.PUBLIC_URL;
const MP4 = window.SPECIMEN_MP4 || `${BASE}/specimen.mp4`;
const WEBM = window.SPECIMEN_WEBM || `${BASE}/specimen.webm`;
const VW = 1280;
const VH = 720;
const SPEC = { x: 0.68, y: 0.34 };
const POS = { x: 0.5, y: 0.3 };

const specimenPoint = (w, h) => {
  const s = Math.max(w / VW, h / VH);
  const rw = VW * s;
  const rh = VH * s;
  return { x: (w - rw) * POS.x + SPEC.x * rw, y: (h - rh) * POS.y + SPEC.y * rh, s };
};

export const SpecimenVideo = ({ phase, progress }) => {
  const ref = useRef(null);
  const st = useRef({ phase, progress });
  st.current = { phase, progress };

  useEffect(() => {
    const c = ref.current;
    const g = c.getContext("2d");
    let raf;
    let packets = [];
    let rings = [];
    let lastRing = 0;
    const resize = () => {
      c.width = c.clientWidth;
      c.height = c.clientHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = (ms) => {
      const t = ms / 1000;
      const w = c.width;
      const h = c.height;
      const { phase, progress } = st.current;
      const { x: cx, y: cy, s } = specimenPoint(w, h);
      const R = 120 * s;
      g.clearRect(0, 0, w, h);
      g.globalCompositeOperation = "lighter";

      const beat = Math.pow(Math.max(0, Math.sin((t * Math.PI * 2) / 2.4)), 6);
      const up = phase === "uploading" && progress > 0;
      const glow = g.createRadialGradient(cx, cy, 0, cx, cy, R * (1.4 + beat * 0.3));
      glow.addColorStop(0, up ? `rgba(0,240,255,${0.12 + progress * 0.25})` : `rgba(150,230,220,${0.03 + beat * 0.12})`);
      glow.addColorStop(1, "rgba(0,0,0,0)");
      g.fillStyle = glow;
      g.fillRect(cx - R * 2, cy - R * 2, R * 4, R * 4);

      if (up) {
        for (let k = 0; k < 2 + progress * 5; k++) {
          const a = Math.random() * 6.28;
          const d = Math.max(w, h) * 0.6;
          packets.push({ x: cx + Math.cos(a) * d, y: cy + Math.sin(a) * d, v: 0.035 + Math.random() * 0.04 });
        }
        if (t - lastRing > 0.9 - progress * 0.6) {
          rings.push({ r: R * 0.4, a: 0.7 });
          lastRing = t;
        }
      }
      packets = packets.filter((p) => Math.hypot(p.x - cx, p.y - cy) > R * 0.35);
      g.strokeStyle = "rgba(0,240,255,0.8)";
      g.lineWidth = 1.5;
      g.beginPath();
      packets.forEach((p) => {
        const nx = p.x + (cx - p.x) * p.v;
        const ny = p.y + (cy - p.y) * p.v;
        g.moveTo(p.x, p.y);
        g.lineTo(nx, ny);
        p.x = nx;
        p.y = ny;
      });
      g.stroke();

      rings = rings.filter((r) => r.a > 0.02);
      rings.forEach((r) => {
        r.r += 3 * s + 2;
        r.a *= 0.95;
        g.strokeStyle = `rgba(0,240,255,${r.a})`;
        g.lineWidth = 2;
        g.beginPath();
        g.arc(cx, cy, r.r, 0, 6.28);
        g.stroke();
      });
      g.globalCompositeOperation = "source-over";
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <div className={`absolute inset-0 specimen-wrap ${phase === "complete" ? "dimmed" : ""}`} data-testid="specimen-video-wrap">
      <video
        data-testid="specimen-video"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ objectPosition: `${POS.x * 100}% ${POS.y * 100}%` }}
        autoPlay
        muted
        loop
        playsInline
      >
        <source src={MP4} type="video/mp4" />
        <source src={WEBM} type="video/webm" />
      </video>
      <div className={`absolute inset-0 upload-tint ${phase === "uploading" ? "on" : ""}`} style={{ opacity: phase === "uploading" ? 0.15 + progress * 0.35 : 0 }} />
      <canvas ref={ref} data-testid="containment-screen-canvas" className="absolute inset-0 w-full h-full" />
    </div>
  );
};
